import { supabase } from "./supabaseClient";

// Existing Supabase resources (do NOT recreate):
// - table:  public."Profiles" (capital P), keyed by id == auth.users.id
// - bucket: private "profile-images", policies scoped to the user's folder
const TABLE = "Profiles";
const BUCKET = "profile-images";

// Profile images render only as small organiser/profile icons, so store a compact
// optimised file: fit within 400×400 preserving aspect ratio (NEVER upscale a
// smaller image), then encode as ~80% WebP. Only this optimised WebP is uploaded —
// the original file is never stored. EXIF orientation is baked in so the icon
// matches how it displayed before; the UI's object-cover framing is unchanged.
async function toProfileWebp(file) {
  const MAX = 400;
  let bitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    bitmap = await createImageBitmap(file);
  }
  const scale = Math.min(1, MAX / bitmap.width, MAX / bitmap.height);
  const w = Math.max(1, Math.round(bitmap.width * scale));
  const h = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(bitmap, 0, 0, w, h);
  return new Promise((resolve, reject) =>
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Could not process image."))),
      "image/webp",
      0.8
    )
  );
}

// Reads the current user's profile row; creates a minimal one (id only) if it
// does not exist yet. Relies on existing RLS to allow own-row select/insert.
export async function getOrCreateProfile(userId) {
  const { data, error } = await supabase
    .from(TABLE)
    .select("*")
    .eq("id", userId)
    .maybeSingle();
  if (error) throw error;
  if (data) return data;

  const { data: created, error: insErr } = await supabase
    .from(TABLE)
    .insert({ id: userId })
    .select()
    .single();
  if (insErr) throw insErr;
  return created;
}

export async function saveProfile(userId, changes) {
  // Only update the fields explicitly provided, so a partial save (e.g. just the
  // profile image) never wipes display name / organiser name / bio.
  const allowed = {};
  if (changes.display_name !== undefined) allowed.display_name = changes.display_name?.trim() || null;
  if (changes.organiser_name !== undefined) allowed.organiser_name = changes.organiser_name?.trim() || null;
  if (changes.organiser_bio !== undefined) allowed.organiser_bio = changes.organiser_bio?.trim() || null;
  if (changes.profile_image_path !== undefined) {
    allowed.profile_image_path = changes.profile_image_path || null;
  }
  const { data, error } = await supabase
    .from(TABLE)
    .update(allowed)
    .eq("id", userId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function uploadProfileImage(userId, file) {
  if (!file) throw new Error("Choose an image.");
  if (!file.type.startsWith("image/")) throw new Error("Only image files are allowed.");
  if (file.size > 5 * 1024 * 1024) throw new Error("Image must be 5 MB or smaller.");

  const optimised = await toProfileWebp(file);
  const path = `${userId}/${crypto.randomUUID()}.webp`;

  const { data, error } = await supabase.storage
    .from(BUCKET)
    .upload(path, optimised, { contentType: "image/webp", cacheControl: "3600", upsert: false });
  if (error) throw error;
  return data.path;
}

export async function getSignedImageUrl(path) {
  if (!path) return null;
  const { data, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(path, 3600);
  if (error) return null;
  return data.signedUrl;
}

// Remove a previous profile image file from the profile-images bucket using its
// exact stored path. Returns null on success, or an error message if blocked.
export async function removeProfileImage(path) {
  if (!path) return null;
  const { error } = await supabase.storage.from(BUCKET).remove([path]);
  if (error) {
    // eslint-disable-next-line no-console
    console.warn("[RoadRnr] previous profile image cleanup failed:", error);
    return error.message || String(error);
  }
  return null;
}
